import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { ChevronUp, Film, Box, Scissors, Sparkles, FolderOpen, X, ImageIcon, Music, Video, FileText, Upload, Loader2, Check } from 'lucide-react'
import { isElectron } from '@/lib/electron'
import { useChatDraft } from '@/hooks/useChatDraft'
import { SUPPORTED_IMAGE_TYPES, resizeImage, getAudioDurationFromFile, getVideoDurationFromFile } from '@/hooks/useWorkflowChat'
import type { ChatAttachment } from '@/hooks/useWorkflowChat'

export const SKILL_STARTERS: Record<string, string> = {
  'narrative-first': "I have a script I'd like to turn into a video.",
  'music-video': 'I want to create a music video.',
  'social-short': 'Create a vertical social media video.',
  'podcast-editing': "I have a podcast episode I'd like to edit down.",
  'dialog-editing': 'I have footage with dialog that needs editing.',
  'cinematic': 'Create something cinematic.',
  'documentary': 'I want a documentary-style video.',
  'anime': 'Create an anime-style video.',
  'commercial': 'I need a polished commercial/ad video.',
}

const DESKTOP_SUGGESTIONS = [
  { icon: FolderOpen, text: 'Create a highlight reel from my local videos' },
  { icon: Scissors, text: 'Edit my footage into a polished social media clip' },
  { icon: Sparkles, text: 'Turn my photos and clips into a cinematic montage' },
]

function getContextualSuggestions(mediaContext: string) {
  const fileCount = (mediaContext.match(/^- /gm) || []).length || 'my'
  const hasDrone = /drone|aerial|DJI|mavic|phantom/i.test(mediaContext)
  const hasInterview = /interview|talking|podcast|conversation/i.test(mediaContext)
  const hasProduct = /product|unbox|review|demo/i.test(mediaContext)
  const hasVacation = /vacation|travel|landscape|trip|holiday|beach|hike/i.test(mediaContext)
  const hasEvent = /wedding|birthday|party|concert|event|ceremony|graduation/i.test(mediaContext)
  const hasScreenRec = /screen|tutorial|walkthrough|recording|capture/i.test(mediaContext)

  if (hasDrone) return [
    { icon: Film, text: `Edit my ${fileCount} drone clips into a cinematic highlight reel with music` },
    { icon: Scissors, text: `Pick the best moments from these drone shots and create a 30-second social clip` },
    { icon: Sparkles, text: `Create a travel montage from my aerial footage` },
  ]
  if (hasInterview) return [
    { icon: Film, text: `Edit these interview clips into a polished talking-head video` },
    { icon: Scissors, text: `Pull the best soundbites from my interviews into a short recap` },
    { icon: Sparkles, text: `Create a documentary-style piece from my interview footage` },
  ]
  if (hasProduct) return [
    { icon: Box, text: `Create a product showcase video from my ${fileCount} clips` },
    { icon: Scissors, text: `Edit these into a quick social media product ad` },
    { icon: Sparkles, text: `Build a polished product launch video` },
  ]
  if (hasVacation) return [
    { icon: Film, text: `Turn my ${fileCount} vacation clips into a travel highlight reel` },
    { icon: Scissors, text: `Create a short travel montage for sharing` },
    { icon: Sparkles, text: `Make a cinematic travel video from my footage` },
  ]
  if (hasEvent) return [
    { icon: Film, text: `Create a polished recap video from this event footage` },
    { icon: Scissors, text: `Cut the best moments into a 60-second shareable highlight` },
    { icon: Sparkles, text: `Make a heartfelt montage from these clips` },
  ]
  if (hasScreenRec) return [
    { icon: Film, text: `Clean up my screen recordings into a polished tutorial` },
    { icon: Scissors, text: `Trim the dead air and make a tight walkthrough` },
    { icon: Sparkles, text: `Add structure and pacing to my recording` },
  ]
  return [
    { icon: Film, text: `Create a highlight reel from my ${fileCount} selected clips` },
    { icon: Scissors, text: `Edit these files into a short, shareable video` },
    { icon: Sparkles, text: `Surprise me -- pick the best moments and make something` },
  ]
}

function getFileKey(f: File) {
  return `${f.name}-${f.size}-${f.lastModified}`
}

const DOC_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-powerpoint', 'application/msword', 'text/plain', 'text/markdown', 'text/csv']

interface WelcomeChatProps {
  onSendMessage: (message: string, opts?: { attachments?: ChatAttachment[]; skill?: string }) => void
  mediaContext?: string
}

export function WelcomeChat({ onSendMessage, mediaContext }: WelcomeChatProps) {
  const {
    input, setInput,
    pendingImages, setPendingImages,
    pendingAudio, setPendingAudio,
    pendingDocuments, setPendingDocuments,
    clearDraft,
  } = useChatDraft('welcome')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const desktop = isElectron()

  const contextSuggestions = useMemo(() => {
    if (mediaContext) return getContextualSuggestions(mediaContext)
    if (desktop) return DESKTOP_SUGGESTIONS
    return null
  }, [mediaContext, desktop])

  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 160) + 'px'
  }, [input])

  // --- Immediate upload logic ---
  const generateUploadUrl = useMutation(api.workflows.generateUploadUrl)
  const getStorageUrl = useMutation(api.workflows.getStorageUrl)
  const processDocumentAction = useAction(api.documentProcessing.processDocument)

  const [uploadStates, setUploadStates] = useState<Record<string, 'uploading' | 'done' | 'error'>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const uploadedRef = useRef<Map<string, ChatAttachment>>(new Map())
  const uploadPromisesRef = useRef<Map<string, Promise<void>>>(new Map())

  const isAnyUploading = useMemo(() => Object.values(uploadStates).some(s => s === 'uploading'), [uploadStates])

  const uploadFile = useCallback(async (file: File) => {
    const key = getFileKey(file)
    if (uploadedRef.current.has(key) || uploadPromisesRef.current.has(key)) return

    setUploadStates(prev => ({ ...prev, [key]: 'uploading' }))

    const promise = (async () => {
      try {
        let processedFile = file

        // Resize/convert images
        if (file.type.startsWith('image/') && !file.type.startsWith('video/')) {
          const forceFormat = !SUPPORTED_IMAGE_TYPES.has(file.type) ? 'image/png' : undefined
          processedFile = await resizeImage(file, forceFormat ? { forceFormat } : undefined)
        }

        // Validate audio
        let audioDuration: number | undefined
        if (file.type.startsWith('audio/')) {
          if (file.size > 50 * 1024 * 1024) throw new Error('Audio too large (max 50MB)')
          audioDuration = await getAudioDurationFromFile(file)
          if (audioDuration < 0.5 || audioDuration > 300) throw new Error('Audio must be 0.5s - 5min')
        }

        // Validate documents
        if (DOC_TYPES.includes(file.type) && file.size > 100 * 1024 * 1024) {
          throw new Error('Document too large (max 100MB)')
        }

        // Upload to Convex storage
        const uploadUrl = await generateUploadUrl()
        const result = await fetch(uploadUrl, {
          method: 'POST',
          body: processedFile,
          headers: { 'Content-Type': processedFile.type },
        })
        if (!result.ok) throw new Error(`Upload failed`)

        const { storageId } = await result.json()
        const url = await getStorageUrl({ storageId })
        if (!url) throw new Error('Could not get storage URL')

        // Build attachment based on type
        let attachment: ChatAttachment
        if (file.type.startsWith('video/')) {
          const videoDuration = await getVideoDurationFromFile(file)
          attachment = { type: 'video', url, storageId, name: file.name, mimeType: file.type, duration: videoDuration }
        } else if (file.type.startsWith('image/')) {
          attachment = { type: 'image', url, storageId, name: file.name }
        } else if (file.type.startsWith('audio/')) {
          attachment = { type: 'audio', url, storageId, name: file.name, duration: audioDuration!, mimeType: file.type }
        } else {
          // Document - extract text
          let extractedText: string | undefined
          let pageCount: number | undefined
          try {
            const extracted = await processDocumentAction({ storageId, filename: file.name, mimeType: file.type })
            extractedText = extracted.extractedText
            pageCount = extracted.pageCount
          } catch { /* non-critical */ }
          attachment = { type: 'document', url, storageId, name: file.name, mimeType: file.type, extractedText, pageCount }
        }

        // Only store result if file wasn't removed while uploading
        if (uploadPromisesRef.current.has(key)) {
          uploadedRef.current.set(key, attachment)
          setUploadStates(prev => ({ ...prev, [key]: 'done' }))
        }
      } catch (e) {
        console.error(`Upload failed for ${file.name}:`, e)
        if (uploadPromisesRef.current.has(key)) {
          setUploadStates(prev => ({ ...prev, [key]: 'error' }))
        }
      }
    })()

    uploadPromisesRef.current.set(key, promise)
    promise.finally(() => uploadPromisesRef.current.delete(key))
  }, [generateUploadUrl, getStorageUrl, processDocumentAction])

  // Start uploads for any files that don't have upload state yet (handles draft restoration)
  useEffect(() => {
    const allFiles = [
      ...pendingImages.map(p => p.file),
      ...pendingAudio.map(p => p.file),
      ...pendingDocuments.map(p => p.file),
    ]
    for (const file of allFiles) {
      uploadFile(file) // guard inside prevents duplicates
    }
  }, [pendingImages, pendingAudio, pendingDocuments, uploadFile])

  const hasPendingFiles = pendingImages.length > 0 || pendingAudio.length > 0 || pendingDocuments.length > 0

  const removeFile = useCallback((file: File) => {
    const key = getFileKey(file)
    uploadedRef.current.delete(key)
    uploadPromisesRef.current.delete(key)
    setUploadStates(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = input.trim()
    if (!trimmed && !hasPendingFiles) return

    // Wait for any in-progress uploads
    const pending = Array.from(uploadPromisesRef.current.values())
    if (pending.length > 0) {
      setIsSubmitting(true)
      await Promise.all(pending)
      setIsSubmitting(false)
    }

    // Collect completed attachments
    const attachments = Array.from(uploadedRef.current.values())

    // Don't send if we had files but all failed to upload and no text
    if (!trimmed && attachments.length === 0) return

    onSendMessage(trimmed, {
      attachments: attachments.length > 0 ? attachments : undefined,
    })
    clearDraft()
    uploadedRef.current.clear()
    setUploadStates({})
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const addFiles = useCallback((fileArray: File[]) => {
    const newVisual = fileArray.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/')).map(file => ({
      file, preview: URL.createObjectURL(file),
    }))
    const newAudio = fileArray.filter(f => f.type.startsWith('audio/')).map(file => ({ file, duration: 0 }))
    const newDocs = fileArray.filter(f => DOC_TYPES.includes(f.type)).map(file => ({ file }))
    if (newVisual.length) setPendingImages(prev => [...prev, ...newVisual])
    if (newAudio.length) setPendingAudio(prev => [...prev, ...newAudio])
    if (newDocs.length) setPendingDocuments(prev => [...prev, ...newDocs])

    // Start uploading immediately
    for (const file of fileArray) {
      if (file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/') || DOC_TYPES.includes(file.type)) {
        uploadFile(file)
      }
    }
  }, [setPendingImages, setPendingAudio, setPendingDocuments, uploadFile])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    addFiles(Array.from(files))
    e.target.value = ''
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    const files: File[] = []
    for (const item of items) {
      const file = item.getAsFile()
      if (file) files.push(file)
    }
    if (files.length > 0) {
      e.preventDefault()
      addFiles(files)
    }
  }

  // Drag-and-drop
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)

  const readEntryRecursive = useCallback(async (entry: FileSystemEntry): Promise<File[]> => {
    if (entry.isFile) {
      return new Promise((resolve) => {
        (entry as FileSystemFileEntry).file(
          (f) => resolve([f]),
          () => resolve([]),
        )
      })
    }
    if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      const entries = await new Promise<FileSystemEntry[]>((resolve) => {
        const all: FileSystemEntry[] = []
        const readBatch = () => {
          reader.readEntries((batch) => {
            if (batch.length === 0) { resolve(all); return }
            all.push(...batch)
            readBatch()
          }, () => resolve(all))
        }
        readBatch()
      })
      const nested = await Promise.all(entries.map(readEntryRecursive))
      return nested.flat()
    }
    return []
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer?.types.includes('Files')) setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragging(false)

    const items = e.dataTransfer?.items
    if (!items?.length) return

    // Use webkitGetAsEntry for folder support
    const entries: FileSystemEntry[] = []
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.()
      if (entry) entries.push(entry)
    }

    if (entries.length > 0) {
      const allFiles = await Promise.all(entries.map(readEntryRecursive))
      const flat = allFiles.flat().filter((f) => !f.name.startsWith('.'))
      if (flat.length > 0) addFiles(flat)
      return
    }

    // Fallback: plain file list
    const files = e.dataTransfer?.files
    if (files?.length) addFiles(Array.from(files))
  }, [addFiles, readEntryRecursive])

  const submitDisabled = isSubmitting || (isAnyUploading && !input.trim())

  return (
    <div
      className="h-full w-full flex items-center justify-center bg-background px-6 border-l border-border overflow-y-auto relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/5 backdrop-blur-[2px] pointer-events-none">
          <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-primary/40 bg-card/90 backdrop-blur-md px-16 py-12">
            <Upload className="h-10 w-10 text-primary" />
            <p className="text-lg font-medium text-foreground">Drop files or folders</p>
            <p className="text-sm text-muted-foreground">Videos, images, audio, and documents</p>
          </div>
        </div>
      )}
      <div className="w-full max-w-3xl flex flex-col items-center py-12">
        <h1 className="text-3xl sm:text-4xl font-medium text-foreground text-center mb-3 tracking-tighter">
          {mediaContext ? 'What should we make?' : desktop ? 'Create from your local media' : 'What do you want to create?'}
        </h1>
        {desktop && !mediaContext && (
          <p className="text-base text-muted-foreground text-center mb-8">
            Drop files into the chat, import folders, or just describe what you want to make.
          </p>
        )}
        {(!desktop || mediaContext) && <div className="mb-5" />}

        <form onSubmit={handleSubmit} className="w-full mb-8">
          <div className="rounded-2xl shadow-sm border border-border bg-card transition-shadow focus-within:shadow-md focus-within:border-ring overflow-hidden">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={desktop && !mediaContext ? 'e.g., Create a cool video from my local media' : 'Describe your vision'}
              rows={2}
              className="w-full px-5 pt-4 pb-3 bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none resize-none"
            />

            <div className="flex items-center gap-4 px-5 pb-4 pt-2">
              {!hasPendingFiles ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-[0.97] transition-all text-sm font-medium"
                >
                  <div className="flex items-center gap-1">
                    <ImageIcon className="w-4 h-4 opacity-80" />
                    <Video className="w-4 h-4 opacity-80" />
                    <Music className="w-4 h-4 opacity-80" />
                    <FileText className="w-4 h-4 opacity-80" />
                  </div>
                  <span>Add media or documents</span>
                </button>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {pendingImages.map((img, i) => {
                    const isVideo = img.file.type.startsWith('video/')
                    const state = uploadStates[getFileKey(img.file)]
                    return (
                      <div key={img.preview} className="relative group">
                        {isVideo ? (
                          <div className="w-10 h-10 rounded-lg border border-border bg-foreground flex items-center justify-center overflow-hidden">
                            <video src={img.preview} className="w-full h-full object-cover" muted />
                            <Video className="w-4 h-4 text-white absolute" />
                          </div>
                        ) : (
                          <img
                            src={img.preview}
                            alt={img.file.name}
                            className="w-10 h-10 object-cover rounded-lg border border-border"
                          />
                        )}
                        {/* Upload state overlay */}
                        {state === 'uploading' && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                            <Loader2 className="w-4 h-4 animate-spin text-white" />
                          </div>
                        )}
                        {state === 'done' && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center ring-1 ring-card">
                            <Check className="w-2 h-2 text-white" strokeWidth={3} />
                          </div>
                        )}
                        {state === 'error' && (
                          <div className="absolute inset-0 flex items-center justify-center bg-red-500/30 rounded-lg">
                            <X className="w-4 h-4 text-red-400" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            URL.revokeObjectURL(img.preview)
                            setPendingImages(prev => prev.filter((_, j) => j !== i))
                            removeFile(img.file)
                          }}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-foreground/80 text-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    )
                  })}
                  {pendingAudio.map((aud, i) => {
                    const state = uploadStates[getFileKey(aud.file)]
                    return (
                      <div key={aud.file.name + i} className="relative group flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border bg-card">
                        {state === 'uploading' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-500" />
                        ) : state === 'done' ? (
                          <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} />
                        ) : state === 'error' ? (
                          <X className="w-3.5 h-3.5 text-red-400" />
                        ) : (
                          <Music className="w-3.5 h-3.5 text-sky-500" />
                        )}
                        <span className="text-xs text-muted-foreground max-w-[80px] truncate">{aud.file.name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setPendingAudio(prev => prev.filter((_, j) => j !== i))
                            removeFile(aud.file)
                          }}
                          className="w-3.5 h-3.5 rounded-full bg-foreground/80 text-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-2 h-2" />
                        </button>
                      </div>
                    )
                  })}
                  {pendingDocuments.map((doc, i) => {
                    const state = uploadStates[getFileKey(doc.file)]
                    return (
                      <div key={doc.file.name + i} className="relative group flex items-center gap-1.5 px-2 py-1 rounded-lg border border-primary/20 bg-primary/5">
                        {state === 'uploading' ? (
                          <Loader2 className="w-3 h-3 animate-spin text-primary" />
                        ) : state === 'done' ? (
                          <Check className="w-3 h-3 text-emerald-500" strokeWidth={3} />
                        ) : state === 'error' ? (
                          <X className="w-3 h-3 text-red-400" />
                        ) : (
                          <span className="text-[10px] font-bold text-primary">{doc.file.name.split('.').pop()?.toUpperCase()?.slice(0, 4)}</span>
                        )}
                        <span className="text-xs text-muted-foreground max-w-[80px] truncate">{doc.file.name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setPendingDocuments(prev => prev.filter((_, j) => j !== i))
                            removeFile(doc.file)
                          }}
                          className="w-3.5 h-3.5 rounded-full bg-foreground/80 text-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-2 h-2" />
                        </button>
                      </div>
                    )
                  })}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-10 h-10 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
                  >
                    <ImageIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,audio/*,.pdf,.pptx,.ppt,.docx,.doc,.txt,.md,.csv"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="flex-1" />

              <button
                type="submit"
                disabled={submitDisabled}
                className={`w-11 h-11 flex items-center justify-center rounded-full transition-all duration-150 ${
                  submitDisabled
                    ? 'bg-primary/50 text-primary-foreground cursor-not-allowed'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.95]'
                }`}
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ChevronUp className="w-5 h-5" strokeWidth={3} />
                )}
              </button>
            </div>
          </div>
        </form>

        {contextSuggestions && (
          <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {contextSuggestions.map(({ icon: Icon, text }) => (
              <button
                key={text}
                onClick={() => onSendMessage(text)}
                className="group text-left p-4 rounded-xl border border-border bg-card text-sm text-muted-foreground hover:border-primary/30 hover:bg-primary/5 hover:text-foreground transition-all duration-200 leading-relaxed shadow-sm"
              >
                <Icon className="w-4 h-4 mb-2 text-muted-foreground/50 group-hover:text-primary transition-colors" />
                {text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
